import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ListVideo } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Button, Badge, Skeleton } from '@vnetwork/ui/primitives';
import { useLiveStreams } from '@/api/media/live-live-streams';
import type { LiveStreamItem } from '@/api/media/live-live-streams-types';
import { streamStatusTone } from '../live-stream-status';
import styles from './DashboardPage.module.css';

const fmtTime = (value: number | string | null | undefined): string => {
  if (!value) return '—';
  const ms = typeof value === 'string' ? Date.parse(value) : value;
  if (!ms || Number.isNaN(ms)) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
    .format(new Date(ms))
    .replace(',', '');
};

interface RecentJobsCardProps {
  serviceId: string;
}

export function RecentJobsCard({ serviceId }: RecentJobsCardProps) {
  const navigate = useNavigate();
  const base = `/services/live/${serviceId}`;

  const { data, isPending, isError } = useLiveStreams(serviceId, 1, 8, undefined, undefined, {
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  const jobs: LiveStreamItem[] = data?.items ?? [];

  const columns = useMemo<ColumnDef<LiveStreamItem, unknown>[]>(
    () => [
      {
        id: 'app_name',
        header: 'App / Stream',
        cell: ({ row }) => (
          <div>
            <div className={styles.cellPrimary}>{row.original.app_name || '—'}</div>
            <div className={styles.cellSecondary}>{row.original.stream_name || '—'}</div>
          </div>
        ),
      },
      {
        id: 'kind',
        header: 'Kind',
        size: 90,
        cell: ({ row }) => {
          const kind = row.original.stream_kind === 'event' ? 'Event' : 'Linear';
          return (
            <Badge tone={kind === 'Event' ? 'info' : 'neutral'} appearance="subtle" size="sm">
              {kind}
            </Badge>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        size: 140,
        cell: ({ row }) => {
          const s = row.original.status ?? '';
          return (
            <StatusPill tone={streamStatusTone(s)}>
              {s || '—'}
            </StatusPill>
          );
        },
      },
      {
        id: 'created_at',
        header: 'Created',
        size: 160,
        cell: ({ row }) => {
          const ts = row.original.provider_metadata?.created ?? row.original.created_at;
          return (
            <span className={styles.cellSecondary}>{fmtTime(ts)}</span>
          );
        },
      },
    ],
    [],
  );

  return (
    <Section
      title="Recent Jobs"
      description="Last 8 live stream jobs — read-only snapshot."
      style={{ marginBottom: 0 }}
      actions={
        <Button
          variant="ghost"
          size="sm"
          endIcon={<ChevronRight size={14} />}
          onClick={() => navigate(`${base}/current-jobs`)}
        >
          View all
        </Button>
      }
    >
      {isPending ? (
        // 8 rows to match per_page=8 — keeps the card height stable when data lands.
        <div className={styles.skeletonList}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={44} />
          ))}
        </div>
      ) : isError ? (
        <Alert tone="warning">Failed to load recent jobs. Please try again later.</Alert>
      ) : (
        <DataTable
          columns={columns}
          data={jobs}
          getRowId={(r) => r.id}
          onRowClick={(r) => navigate(`${base}/current-jobs/${r.id}`)}
          emptyState={
            <EmptyState
              icon={<ListVideo size={20} />}
              title="No jobs found"
              description="Live stream jobs will appear here once created."
              size="sm"
            />
          }
        />
      )}
    </Section>
  );
}
